import { json as jsonLang } from '@codemirror/lang-json';
import { FrameNotFound } from '@Components/errorHandling';
import { AUDITING_API } from '@Constants/backendConstants';
import { actionEventLogColumns } from '@Constants/TableColumns';
import { useListApi } from '@Hooks/useListApi';
import { usePermissionsRepository } from '@Hooks/useRepository';
import {
    type ApiActionEventLog,
    type ApiCommandAuditLog,
    getBadgeVariantByCommandOutcome,
    getEnumCommandOutcomeLabel,
} from '@Model';
import { ActionEventLogsRepository, CommandAuditLogsRepository } from '@Repositories/Auditing';
import {
    Card,
    DateInputBase,
    EdgeSpacer,
    EnumFilterExpressionOperation,
    EnumOperation,
    type FilterCondition,
    FlexColumn,
    FrameHeader,
    Grid,
    GridColumns,
    Index,
    type ListRequest,
    LoadingFrame,
    ScrollContainer,
    TableWithStoredSettings,
    TextInputBase,
    useFrameEditBaseNavigation,
    useOnTheFlyModal,
    useServerObject,
} from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import CodeMirror from '@uiw/react-codemirror';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import frameClasses from '../readOnlyFrameContent.module.css';
import { FrameEditActionEventLog } from './FrameEditActionEventLog';

export interface FrameEditCommandAuditLogProps {
    /**
     * When set, this row PK is used instead of the route param.
     * Enables opening the frame inside an on-the-fly modal from other views.
     */
    primaryKey?: string;
    /**
     * Alternative lookup key: fetch the row by <c>InvocationId</c> (the correlation UUID that
     * lives on <c>WorkflowRunStep</c> and <c>ActionEventLog</c> as <c>CommandInvocationId</c>)
     * instead of the row PK. Mutually exclusive with {@link primaryKey}.
     */
    invocationId?: string;
    /**
     * When true the header back button delegates to {@link onClose} instead of route navigation.
     */
    embedded?: boolean;
    /**
     * Called when the header back button is used in an embedded context.
     * Only relevant when {@link embedded} is true.
     */
    onClose?: () => void;
}

const COMMAND_AUDIT_LOGS_PATH = `${AUDITING_API}/command_audit_logs`;

export function FrameEditCommandAuditLog(props: FrameEditCommandAuditLogProps = {}) {
    // Callers open the frame either by row PK (primaryKey or route :itemId) or by the semantic
    // InvocationId correlation UUID. Both variants render the same body, but they load the row
    // through different hooks, so each lookup lives in its own component.
    if (props.invocationId !== undefined) {
        return (
            <CommandAuditLogByInvocationId
                invocationId={props.invocationId}
                embedded={props.embedded}
                onClose={props.onClose}
            />
        );
    }
    return (
        <CommandAuditLogByPrimaryKey primaryKey={props.primaryKey} embedded={props.embedded} onClose={props.onClose} />
    );
}

interface CommandAuditLogByPrimaryKeyProps {
    primaryKey?: string;
    embedded?: boolean;
    onClose?: () => void;
}

// @ai-generated 2026-08-31T10:26:48+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
function CommandAuditLogByPrimaryKey({ primaryKey, embedded, onClose }: CommandAuditLogByPrimaryKeyProps) {
    const { itemId } = useParams();
    const lookupKey = primaryKey ?? itemId ?? '';

    const { result, isSuccess } = useServerObject<ApiCommandAuditLog>({
        apiResourcePath: COMMAND_AUDIT_LOGS_PATH,
        primaryKey: lookupKey,
        include: ['all'],
    });

    if (isSuccess === null) {
        return <LoadingFrame />;
    }
    if (!isSuccess) {
        return (
            <FrameNotFound
                apiResourcePath={COMMAND_AUDIT_LOGS_PATH}
                primaryKey={lookupKey}
                result={result}
                embedded={embedded ?? false}
            />
        );
    }

    return <FrameEditCommandAuditLogBody data={result.data} embedded={embedded} onClose={onClose} />;
}

interface CommandAuditLogByInvocationIdProps {
    invocationId: string;
    embedded?: boolean;
    onClose?: () => void;
}

/**
 * InvocationId is unique per row, so the generic list query filtered on it resolves to at most
 * one match. The filter is built here instead of behind a dedicated backend route: the REST API
 * only needs to expose the row by its PK plus the generic query endpoint.
 *
 * @ai-generated 2026-08-31T10:26:48+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
 */
function CommandAuditLogByInvocationId({ invocationId, embedded, onClose }: CommandAuditLogByInvocationIdProps) {
    const auditLogRepository = usePermissionsRepository(CommandAuditLogsRepository);

    const { data: result, isPending } = useQuery({
        queryKey: [COMMAND_AUDIT_LOGS_PATH, 'invocation_id', invocationId],
        queryFn: () => auditLogRepository.getListPost(buildInvocationIdQuery(invocationId), 'all'),
        refetchOnWindowFocus: false,
    });

    if (isPending) {
        return <LoadingFrame />;
    }

    const commandAuditLog = result?.data?.items[0];
    if (commandAuditLog === undefined) {
        return (
            <FrameNotFound
                apiResourcePath={COMMAND_AUDIT_LOGS_PATH}
                primaryKey={invocationId}
                result={result ?? { data: undefined, messages: [] }}
                embedded={embedded ?? false}
            />
        );
    }

    return <FrameEditCommandAuditLogBody data={commandAuditLog} embedded={embedded} onClose={onClose} />;
}

// @ai-generated 2026-08-31T10:26:48+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
function buildInvocationIdQuery(invocationId: string): ListRequest {
    const invocationIdFieldName: Index<ApiCommandAuditLog> = 'invocation_id';
    return {
        page_no: 0,
        page_size: 1,
        full_text_search: '',
        sort_columns: [],
        filter_columns: {
            operation: EnumFilterExpressionOperation.And,
            filters: [{ field: invocationIdFieldName, value: invocationId, operator: EnumOperation.Equal }],
        },
    };
}

interface FrameEditCommandAuditLogBodyProps {
    data: ApiCommandAuditLog;
    embedded?: boolean;
    onClose?: () => void;
}

const lockedOnChange = () => false;

function FrameEditCommandAuditLogBody({ data, embedded, onClose }: FrameEditCommandAuditLogBodyProps) {
    const { onBack: routeBack } = useFrameEditBaseNavigation();
    const onBack = embedded ? onClose : routeBack;
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();

    // The correlation filter is applied client-side on top of whatever the user filters in the
    // table, so the generic action-event-log query endpoint is all the backend has to expose.
    const { loadPage: loadRelatedActionEvents } = useListApi(ActionEventLogsRepository, AUDITING_API);

    const relatedActionEventFilters = useMemo<FilterCondition[]>(
        () => [{ field: 'command_invocation_id', value: data.invocation_id, operator: EnumOperation.Equal }],
        [data.invocation_id],
    );

    const openActionEventModal = useCallback(
        (actionEvent: ApiActionEventLog) => {
            let modalId = '';
            modalId = openOnTheFlyModal({
                child: (
                    <FrameEditActionEventLog
                        primaryKey={actionEvent.id}
                        embedded
                        onClose={() => closeOnTheFlyModals(modalId)}
                    />
                ),
            });
            return true;
        },
        [openOnTheFlyModal, closeOnTheFlyModals],
    );

    const relatedTableSorting = useMemo(() => ({ columnName: 'created_at' as const, direction: 'desc' as const }), []);

    const jsonExtensions = useMemo(() => [jsonLang()], []);
    const inputPretty = useMemo(() => prettyJson(data.input), [data.input]);
    const outputPretty = useMemo(() => prettyJson(data.output), [data.output]);

    return (
        <FlexColumn gap="none" height="100%" width="100%">
            <FrameHeader label="Command" resourceLabel="Command" onBack={onBack} />
            <ScrollContainer width="100%" height="100%">
                <FlexColumn gap="sm" className={frameClasses.content}>
                    <Card
                        name="HeadData"
                        label="Command"
                        badges={[
                            {
                                children: getEnumCommandOutcomeLabel(data.outcome),
                                variant: getBadgeVariantByCommandOutcome(data.outcome),
                            },
                        ]}
                    >
                        <Grid columns={12}>
                            <GridColumns spans={[3, 5, 4]}>
                                <DateInputBase
                                    name="created_at"
                                    label="Zeitpunkt"
                                    value={data.created_at}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="created_by"
                                    label="Benutzer-ID"
                                    value={data.created_by}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="name"
                                    label="Command"
                                    value={data.name}
                                    onChange={lockedOnChange}
                                    locked
                                />
                            </GridColumns>
                        </Grid>
                    </Card>
                    <Card name="Correlation" label="Korrelation">
                        <Grid columns={12}>
                            <GridColumns spans={[6, 6]}>
                                <TextInputBase
                                    name="invocation_id"
                                    label="Aufruf-ID"
                                    value={data.invocation_id}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="workflow_run_id"
                                    label="Workflow-Run-ID"
                                    value={data.workflow_run_id ?? ''}
                                    onChange={lockedOnChange}
                                    locked
                                />
                            </GridColumns>
                            <GridColumns spans={[6, 6]}>
                                <TextInputBase
                                    name="trace_id"
                                    label="Trace ID"
                                    value={data.trace_id}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="span_id"
                                    label="Span ID"
                                    value={data.span_id}
                                    onChange={lockedOnChange}
                                    locked
                                />
                            </GridColumns>
                        </Grid>
                    </Card>
                    <Card name="Input" label="Input">
                        <CodeMirror value={inputPretty} height="300px" extensions={jsonExtensions} editable={false} />
                    </Card>
                    <Card name="Output" label="Output">
                        <CodeMirror value={outputPretty} height="300px" extensions={jsonExtensions} editable={false} />
                    </Card>
                    <Card name="RelatedActionEvents" label="Verknüpfte Action Logs">
                        <TableWithStoredSettings<ApiActionEventLog>
                            name="CommandAuditLogRelatedActionEvents"
                            columns={actionEventLogColumns}
                            additionalColumnFilters={relatedActionEventFilters}
                            initialSorting={relatedTableSorting}
                            loader={loadRelatedActionEvents}
                            canCreate={false}
                            onSelect={openActionEventModal}
                        />
                    </Card>
                </FlexColumn>
                <EdgeSpacer />
            </ScrollContainer>
        </FlexColumn>
    );
}

// Best-effort JSON pretty-printer. Returns the input verbatim if it isn't parseable JSON.
function prettyJson(raw: string): string {
    if (!raw) {
        return '';
    }
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
}
